import { INSERT_ZONE_WIDTH, STEP_WIDTH } from './constants'

export const flattenSteps = (journeys = []) =>
    journeys.flatMap((journey) =>
        journey.steps.map((step) => ({
            ...step,
            journeyId: journey.id,
            journeyTitle: journey.title,
        }))
    )

export const parseCellId = (droppableId = '') => {
    const parts = String(droppableId).split('|')
    if (parts[0] !== 'cell' || parts.length !== 3) return null
    return { releaseId: parts[1], stepId: parts[2] }
}

export const cellId = (releaseId, stepId) =>
    `cell|${String(releaseId)}|${String(stepId)}`

export const isSameStoryCell = (sourceCell, destCell) => {
    if (!sourceCell || !destCell) return false

    return (
        String(sourceCell.stepId) === String(destCell.stepId) &&
        String(sourceCell.releaseId) === String(destCell.releaseId)
    )
}

export const reorderStoriesInCell = (stories, stepId, sourceIndex, destIndex) => {
    const normalizedStepId = String(stepId)
    const cellStories = stories.filter(
        (story) => String(story.stepId) === normalizedStepId
    )
    const cellIds = new Set(cellStories.map((story) => String(story.id)))
    const reordered = [...cellStories]
    const [moved] = reordered.splice(sourceIndex, 1)
    reordered.splice(destIndex, 0, moved)

    let cellCursor = 0
    return stories.map((story) => {
        if (!cellIds.has(String(story.id))) return story
        const next = reordered[cellCursor]
        cellCursor += 1
        return next
    })
}

export const stepLaneId = (journeyId) => `steps|${journeyId}`

export const stepLaneEndId = (journeyId) => `steps-end|${journeyId}`

export const parseStepLaneId = (droppableId = '') => {
    const parts = String(droppableId).split('|')
    if (parts[0] !== 'steps' || parts.length !== 2) return null
    return parts[1]
}

export const parseStepLaneEndId = (droppableId = '') => {
    const parts = String(droppableId).split('|')
    if (parts[0] !== 'steps-end' || parts.length !== 2) return null
    return parts[1]
}

export const parseStepLabel = (title = '') => {
    const match = String(title).match(/^(\d+\.\d+)\s+(.+)$/i)
    if (match) {
        return { version: match[1], name: match[2] }
    }
    const parts = String(title).trim().split(/\s+/)
    return {
        version: parts[0] || '1.0',
        name: parts.slice(1).join(' ') || 'Untitled step',
    }
}

export const parseJourneyLabel = (title = '') =>
    String(title).replace(/^\d+\.\s*/, '').trim() || 'Untitled journey'

export const isUntitledName = (text = '') => {
    const normalized = String(text).trim().toLowerCase()
    return !normalized || normalized.startsWith('untitled')
}

export const resolveEditTitleName = (
    draft,
    draftRef,
    inputRef,
    untitledLabel
) => {
    let raw
    if (draft !== undefined && draft !== null) {
        raw = draft
    } else {
        raw = inputRef?.current?.value ?? draftRef?.current ?? ''
    }

    const trimmed = String(raw).trim()
    return trimmed || untitledLabel
}

const getCount = (item, countKey, listKey) => {
    const count = item?.[countKey]
    if (typeof count === 'number' && count > 0) return count
    const list = item?.[listKey]
    if (Array.isArray(list) && list.length > 0) return list.length
    return 0
}

export const isMetaFlagTrue = (value) =>
    value === true || value === 'true' || value === 1 || value === '1'

export const normalizeHexColor = (value) => {
    const raw = String(value ?? '').trim()
    if (!raw) return ''
    return raw.startsWith('#') ? raw : `#${raw}`
}

const DEFAULT_CARD_COLORS = new Set(['#fff', '#ffffff', 'white'])

export const isDefaultCardColor = (color) => {
    const normalized = normalizeHexColor(color).toLowerCase()
    if (!normalized) return true
    return DEFAULT_CARD_COLORS.has(normalized)
}

export const DEFAULT_STEP_ACCENT = '#7c3aed'
export const DEFAULT_STORY_ACCENT = '#db2777'
export const DEFAULT_JOURNEY_ACCENT = '#6366f1'
export const DEFAULT_RELEASE_ACCENT = '#f97316'

export const getDefaultCardAccentColor = (cardType) => {
    if (cardType === 'step') return DEFAULT_STEP_ACCENT
    if (cardType === 'journey') return DEFAULT_JOURNEY_ACCENT
    if (cardType === 'release') return DEFAULT_RELEASE_ACCENT
    return DEFAULT_STORY_ACCENT
}

export const STORY_STATUS_OPTIONS = [
    { value: 'not_ready', label: 'Not ready', apiValue: 'NOT READY' },
    { value: 'in_progress', label: 'In progress', apiValue: 'IN PROGRESS' },
    { value: 'done', label: 'Done', apiValue: 'DONE' },
]

export const DEFAULT_STORY_STATUS = 'not_ready'

export const buildBoardNavigationContext = (journeys = [], releases = []) => ({
    journeys: journeys.map((journey) => ({
        id: String(journey.id),
        title: parseJourneyLabel(journey.title) || 'Untitled journey',
        steps: (journey.steps ?? []).map((step) => ({
            id: String(step.id),
            title: parseStepLabel(step.title).name || 'Untitled step',
        })),
    })),
    releases: releases.map((item, index) => ({
        id: String(item.id),
        title: item.title || `Release ${index + 1}`,
        index,
    })),
})

export const isCommentTimeBoardFilter = (appliedFilters = {}) =>
    Boolean(
        (appliedFilters.from && appliedFilters.to) ||
            (appliedFilters.commentFilter &&
                appliedFilters.commentFilter !== 'all' &&
                appliedFilters.commentFilter !== 'custom')
    )

export const itemHasOwnComments = (item = {}) =>
    isMetaFlagTrue(item.comments ?? item.comment) ||
    Boolean(item.hasComments) ||
    Number(item.commentsCount) > 0

/** Parents must not inherit child comment matches (e.g. Today on journey with no comment). */
export const resolveBoardItemMatchesFilter = (
    item,
    cardType,
    appliedFilters = {}
) => {
    const filters = appliedFilters || {}
    const search = String(filters.search ?? '').trim().toLowerCase()

    // Search should also match release / journey / step / story titles client-side
    // when the API omits matchesFilter for that entity (esp. releases).
    if (search) {
        const rawTitle = String(
            item?.name ?? item?.title ?? ''
        ).toLowerCase()
        if (rawTitle.includes(search)) return true
    }

    const apiMatch = Boolean(item?.matchesFilter)
    if (!apiMatch) return false

    if (cardType === 'story') return true

    if (isCommentTimeBoardFilter(filters)) {
        if (filters.commentFilter === 'today') {
            return Boolean(item.todayComment)
        }
        return itemHasOwnComments(item)
    }

    if (filters.hasComments) {
        return itemHasOwnComments(item)
    }

    return apiMatch
}

export const stripBubbledCommentMeta = (item, mappedFields, appliedFilters = {}) => {
    const filters = appliedFilters || {}
    if (!isCommentTimeBoardFilter(filters) && !filters.hasComments) {
        return mappedFields
    }

    const ownComments =
        filters.commentFilter === 'today'
            ? Boolean(item.todayComment)
            : itemHasOwnComments(item)

    if (ownComments) return mappedFields

    return {
        ...mappedFields,
        showCommentsIcon: false,
        hasComments: false,
        commentsCount: 0,
    }
}

export const countBoardFilterMatches = (board) => {
    if (!board?.isFilterActive) return 0

    let count = 0

    for (const journey of board.journeys ?? []) {
        if (journey.matchesFilter === true) count += 1
        for (const step of journey.steps ?? []) {
            if (step.matchesFilter === true) count += 1
        }
    }

    for (const release of board.releases ?? []) {
        if (release.matchesFilter === true) count += 1
        for (const story of release.stories ?? []) {
            if (story.matchesFilter === true) count += 1
        }
    }

    return count
}

/** Data attributes only — CSS module class names are hashed in the DOM. */
export const FILTER_MATCH_SELECTOR = '[data-story-map-filter-match="true"]'

export const getFilterMatchSelector = () => FILTER_MATCH_SELECTOR

export const getFilterMatchCardId = (node) => {
    if (!node) return null
    return (
        node.getAttribute('data-story-map-journey-id') ||
        node.getAttribute('data-story-map-release-id') ||
        node.getAttribute('data-story-map-story-id') ||
        node.getAttribute('data-story-map-step-id') ||
        node.querySelector('[data-story-map-journey-id]')?.getAttribute(
            'data-story-map-journey-id'
        ) ||
        node.querySelector('[data-story-map-release-id]')?.getAttribute(
            'data-story-map-release-id'
        ) ||
        node.querySelector('[data-story-map-story-id]')?.getAttribute(
            'data-story-map-story-id'
        ) ||
        node.querySelector('[data-story-map-step-id]')?.getAttribute(
            'data-story-map-step-id'
        ) ||
        null
    )
}

/** CSS module class for filter highlight/dim when API sets matchesFilter. */
export const getStoryMapFilterClass = (isFilterActive, matchesFilter, stylesModule) => {
    if (!isFilterActive || matchesFilter === undefined || matchesFilter === null) {
        return ''
    }
    return matchesFilter
        ? stylesModule.storyMapFilterMatch
        : stylesModule.storyMapFilterDim
}

export const normalizeStoryStatus = (value) => {
    const trimmed = String(value ?? '').trim()
    if (!trimmed) return DEFAULT_STORY_STATUS

    const upper = trimmed.toUpperCase().replace(/_/g, ' ')
    if (upper === 'NOT READY') return 'not_ready'
    if (upper === 'IN PROGRESS') return 'in_progress'
    if (upper === 'DONE' || upper === 'COMPLETED' || upper === 'COMPLETE') {
        return 'done'
    }

    const raw = trimmed.toLowerCase().replace(/[\s-]+/g, '_')
    if (raw === 'not_ready' || raw === 'notready') return 'not_ready'
    if (raw === 'in_progress' || raw === 'inprogress') return 'in_progress'
    if (raw === 'done' || raw === 'completed' || raw === 'complete') return 'done'

    return STORY_STATUS_OPTIONS.some((option) => option.value === raw)
        ? raw
        : DEFAULT_STORY_STATUS
}

export const toStoryStatusApiValue = (value) => {
    const normalized = normalizeStoryStatus(value)
    return (
        STORY_STATUS_OPTIONS.find((option) => option.value === normalized)
            ?.apiValue ?? 'NOT READY'
    )
}

/** Whether a story status is included in the active storyStatus filter values. */
export const storyMatchesStatusFilter = (status, storyStatusFilter = []) => {
    if (!Array.isArray(storyStatusFilter) || storyStatusFilter.length === 0) {
        return null
    }

    const normalized = normalizeStoryStatus(status)
    const apiValue = toStoryStatusApiValue(normalized)

    return storyStatusFilter.some((entry) => {
        const entryNormalized = normalizeStoryStatus(entry)
        const entryApi = toStoryStatusApiValue(entryNormalized)
        return (
            entryNormalized === normalized ||
            String(entry) === apiValue ||
            entryApi === apiValue
        )
    })
}

/**
 * Recompute matchesFilter after a local story field update while board filters
 * are active (e.g. status In progress → Done should blur under an In progress filter).
 */
export const recomputeStoryMatchesFilterAfterUpdate = (
    story = {},
    updates = {},
    filters = {},
    isFilterActive = false
) => {
    if (!isFilterActive) return story?.matchesFilter

    const nextStatus =
        updates.status !== undefined ? updates.status : story.status
    const statusMatch = storyMatchesStatusFilter(
        nextStatus,
        filters?.storyStatus
    )

    if (statusMatch === false) return false

    const hasOnlyStatusFilter =
        Array.isArray(filters?.storyStatus) &&
        filters.storyStatus.length > 0 &&
        !String(filters?.search ?? '').trim() &&
        !filters?.hasComments &&
        !filters?.hasAttachment &&
        !filters?.hasDescription &&
        !filters?.journeyId &&
        !filters?.releaseId &&
        !isCommentTimeBoardFilter(filters)

    if (statusMatch === true && hasOnlyStatusFilter) return true
    if (statusMatch === true && story?.matchesFilter === true) return true

    return story?.matchesFilter
}

export const getStoryStatusLabel = (value) =>
    STORY_STATUS_OPTIONS.find(
        (option) => option.value === normalizeStoryStatus(value)
    )?.label ?? 'Not ready'

export const mapStoryStatusFields = (item = {}) => ({
    status: normalizeStoryStatus(item.status),
})

const hexToRgb = (hex) => {
    const normalized = normalizeHexColor(hex).replace('#', '')
    const value =
        normalized.length === 3
            ? normalized
                  .split('')
                  .map((char) => char + char)
                  .join('')
            : normalized

    if (!/^[0-9a-f]{6}$/i.test(value)) return null

    return {
        r: parseInt(value.slice(0, 2), 16),
        g: parseInt(value.slice(2, 4), 16),
        b: parseInt(value.slice(4, 6), 16),
    }
}

export const getCardColorStyle = (color) => {
    if (!color || isDefaultCardColor(color)) return undefined

    const normalized = normalizeHexColor(color)
    const rgb = hexToRgb(normalized)
    if (!rgb) return undefined

    return {
        borderTopColor: normalized,
        borderTopWidth: '3px',
        borderLeftColor: `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.42)`,
        borderRightColor: `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.42)`,
        borderBottomColor: `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.42)`,
        backgroundColor: `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.14)`,
    }
}

export const mapCardColorFields = (item = {}) => {
    const rawColor = item.color ?? item.accentColor
    if (!rawColor || typeof rawColor !== 'string') return {}

    const color = normalizeHexColor(rawColor)
    return {
        color,
        accentColor: color,
    }
}

export const mapApiMetaToCardFields = (item = {}) => {
    const fields = {}

    const descriptionFlag = item.description
    const commentsFlag = item.comments ?? item.comment
    const attachmentFlag = item.attachment

    if (isMetaFlagTrue(descriptionFlag)) {
        fields.showDescriptionIcon = true
        fields.hasDescription = true
    } else if (typeof descriptionFlag === 'string' && descriptionFlag.trim()) {
        fields.description = descriptionFlag
        fields.showDescriptionIcon = true
        fields.hasDescription = true
    } else if (item.hasDescription || item.showDescriptionIcon) {
        fields.showDescriptionIcon = true
        fields.hasDescription = true
        if (typeof item.description === 'string') {
            fields.description = item.description
        }
    }

    if (isMetaFlagTrue(commentsFlag)) {
        fields.showCommentsIcon = true
        fields.hasComments = true
        fields.commentsCount = Math.max(Number(item.commentsCount) || 0, 1)
    } else if (Number(item.commentsCount) > 0) {
        fields.showCommentsIcon = true
        fields.hasComments = true
        fields.commentsCount = item.commentsCount
    } else if (item.hasComments || item.showCommentsIcon) {
        fields.showCommentsIcon = true
        fields.hasComments = true
    } else if (Array.isArray(item.comments) && item.comments.length > 0) {
        fields.showCommentsIcon = true
        fields.hasComments = true
        fields.commentsCount = item.comments.length
    }

    if (isMetaFlagTrue(attachmentFlag)) {
        fields.showAttachmentIcon = true
        fields.hasAttachment = true
        fields.attachmentsCount = Math.max(Number(item.attachmentsCount) || 0, 1)
    } else if (Array.isArray(item.attachments) && item.attachments.length > 0) {
        fields.showAttachmentIcon = true
        fields.hasAttachment = true
        fields.attachmentsCount = item.attachments.length
    } else if (Number(item.attachmentsCount) > 0) {
        fields.showAttachmentIcon = true
        fields.hasAttachment = true
        fields.attachmentsCount = item.attachmentsCount
    } else if (item.hasAttachment || item.showAttachmentIcon) {
        fields.showAttachmentIcon = true
        fields.hasAttachment = true
    }

    if (item.likesCount != null) {
        fields.likesCount = item.likesCount
    }

    return fields
}

const hasDescriptionMeta = (item = {}) =>
    Boolean(item.showDescriptionIcon) ||
    Boolean(item.hasDescription) ||
    isMetaFlagTrue(item.description) ||
    (typeof item.description === 'string' &&
        String(item.description).replace(/<[^>]*>/g, '').trim().length > 0)

const hasCommentsMeta = (item = {}) =>
    Boolean(item.showCommentsIcon) ||
    Boolean(item.hasComments) ||
    isMetaFlagTrue(item.comments) ||
    isMetaFlagTrue(item.comment) ||
    getCount(item, 'commentsCount', 'comments') > 0

const hasAttachmentsMeta = (item = {}) =>
    Boolean(item.showAttachmentIcon) ||
    Boolean(item.hasAttachment) ||
    isMetaFlagTrue(item.attachment) ||
    getCount(item, 'attachmentsCount', 'attachments') > 0

export const getCardMetaIcons = (item = {}) => {
    const icons = []

    if (hasDescriptionMeta(item)) {
        icons.push({
            key: 'description',
            icon: 'mdi:file-document-outline',
            label: 'Description',
        })
    }

    if (hasAttachmentsMeta(item)) {
        icons.push({
            key: 'attachments',
            icon: 'mdi:paperclip',
            label: 'Attachments',
        })
    }

    if (hasCommentsMeta(item)) {
        icons.push({
            key: 'comments',
            icon: 'mdi:comment-outline',
            label: 'Comments',
        })
    }

    if (getCount(item, 'likesCount', 'likes') > 0) {
        icons.push({
            key: 'likes',
            icon: 'mdi:thumb-up-outline',
            label: 'Likes',
        })
    }

    return icons
}

export const buildStepLaneColumns = (stepCount) => {
    if (stepCount <= 1) {
        return 'minmax(0, var(--sb-step-width, 200px))'
    }

    return `repeat(${stepCount - 1}, minmax(0, var(--sb-step-width, 200px)) minmax(0, var(--sb-insert-width, 28px))) minmax(0, var(--sb-step-width, 200px))`
}

export const getStepGridColumn = (stepIndex) => stepIndex * 2 + 1

export const getStepInsertGridColumn = (insertIndex) => insertIndex * 2 + 2

export const getJourneySpan = (stepCount) =>
    stepCount > 1 ? stepCount * 2 - 1 : 1

export const getJourneyNumber = (journeys, journeyId) => {
    const normalizedId = String(journeyId)
    const index = journeys.findIndex(
        (journey) => String(journey.id) === normalizedId
    )
    return index >= 0 ? String(index + 1) : '1'
}

export const renumberJourneySteps = (journey, journeys) => {
    const journeyNumber = getJourneyNumber(journeys, journey.id)
    return {
        ...journey,
        steps: journey.steps.map((step, idx) => ({
            ...step,
            title: `${journeyNumber}.${idx + 1} ${step.title.replace(/^\d+\.\d+\s*/, '')}`,
        })),
    }
}

export const isStepWidthTrack = (track) =>
    track?.type === 'step' || track?.type === 'emptyStep'

export const buildBoardTracks = (journeys = []) => {
    const tracks = []

    journeys.forEach((journey, journeyIndex) => {
        if (journey.steps.length === 0) {
            tracks.push({
                type: 'emptyStep',
                journeyId: journey.id,
                journeyIndex,
            })
        } else {
            journey.steps.forEach((step, stepIndex) => {
                tracks.push({
                    type: 'step',
                    step,
                    journey,
                    journeyId: journey.id,
                    stepIndex,
                    journeyIndex,
                })

                if (stepIndex < journey.steps.length - 1) {
                    tracks.push({
                        type: 'gap',
                        gapKind: 'step',
                        journeyId: journey.id,
                        insertStepIndex: stepIndex + 1,
                        journeyIndex,
                    })
                }
            })
        }

        if (journeyIndex < journeys.length - 1) {
            tracks.push({
                type: 'gap',
                gapKind: 'journey',
                journeyId: journey.id,
                insertJourneyIndex: journeyIndex + 1,
                insertStepIndex: journey.steps.length,
                journeyIndex,
            })
        }
    })

    if (journeys.length > 0) {
        const lastJourney = journeys[journeys.length - 1]
        tracks.push({
            type: 'gap',
            gapKind: 'trailing',
            journeyId: lastJourney.id,
            insertJourneyIndex: journeys.length,
            insertStepIndex: lastJourney.steps.length,
            journeyIndex: journeys.length - 1,
        })
    }

    return tracks
}

export const buildGridTemplateColumns = (tracks = []) =>
    tracks
        .map((track) =>
            isStepWidthTrack(track)
                ? `var(--sb-step-width, ${STEP_WIDTH}px)`
                : `var(--sb-insert-width, ${INSERT_ZONE_WIDTH}px)`
        )
        .join(' ')

export const getJourneyBandClass = (journeyIndex, stylesModule) =>
    journeyIndex % 2 === 0
        ? stylesModule.columnBandOdd
        : stylesModule.columnBandEven

export const getColumnBandClass = (tracks, trackIndex, stylesModule) => {
    const track = tracks[trackIndex]
    if (!isStepWidthTrack(track)) return ''

    const journeyIndex =
        typeof track.journeyIndex === 'number' ? track.journeyIndex : 0

    return getJourneyBandClass(journeyIndex, stylesModule)
}

const bandClassToColor = (bandClass, stylesModule) => {
    if (bandClass === stylesModule.columnBandEven) return 'var(--sb-column-even)'
    if (bandClass === stylesModule.columnBandOdd) return 'var(--sb-column-odd)'
    return 'var(--sb-canvas)'
}

const getAdjacentStepBandClasses = (tracks, trackIndex, stylesModule) => {
    let leftClass = ''
    let rightClass = ''

    for (let index = trackIndex - 1; index >= 0; index -= 1) {
        if (isStepWidthTrack(tracks[index])) {
            leftClass = getColumnBandClass(tracks, index, stylesModule)
            break
        }
    }

    for (let index = trackIndex + 1; index < tracks.length; index += 1) {
        if (isStepWidthTrack(tracks[index])) {
            rightClass = getColumnBandClass(tracks, index, stylesModule)
            break
        }
    }

    return { leftClass, rightClass }
}

export const getGapSplitStyle = (tracks, trackIndex, stylesModule) => {
    const { leftClass, rightClass } = getAdjacentStepBandClasses(
        tracks,
        trackIndex,
        stylesModule
    )

    return {
        '--gap-color-left': bandClassToColor(leftClass, stylesModule),
        '--gap-color-right': bandClassToColor(rightClass, stylesModule),
    }
}

export const parseReleaseDates = (dateRange = '') => {
    const parts = String(dateRange).split(/\s*-\s*/)
    if (parts.length >= 2) {
        return {
            start: parts[0].trim(),
            due: parts.slice(1).join(' - ').trim(),
        }
    }
    return { start: null, due: null }
}
